import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { BioscopeRestart, BioscopeRouterRestart } from '@/domain/bioscopeRestarts';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './BioscopeRestarts.module.scss';

export function getCurrentBioscopeRestartStep(t: TFunction, restart: BioscopeRestart): string {
    if (restart.isDone) {
        return '';
    }

    if (restart.shutdownSmsId && restart.startSmsId) {
        return t('molecules.bioscopeRestarts.waitingForCabinetToStart');
    }

    if (restart.shutdownSmsId) {
        return t('molecules.bioscopeRestarts.waitingForCabinetToStop');
    }

    return t('molecules.bioscopeRestarts.waitingForCabinetStatus');
}

export function getCurrentBioscopeRouterRestartStep(
    t: TFunction,
    restart: BioscopeRouterRestart
): string {
    if (restart.isDone) {
        return '';
    }

    if (!restart.rebootSmsId) {
        return t('bioscopeDetail.restarts.waitingRouterStop');
    }

    if (!restart.rebootResponseId) {
        return t('bioscopeDetail.restarts.waitingRouterStart');
    }

    return t('bioscopeDetail.restarts.waitingRouterStatus');
}

export interface BioscopeRestartsProps<TRestart extends BioscopeRestart | BioscopeRouterRestart> {
    loading: boolean;
    lastBioscopeRestarts: TRestart[] | undefined;
    restartBioscope: () => Promise<unknown>;
    stepMessage: (t: TFunction, restart: TRestart) => string;
    disabled?: boolean;
    disabledReason?: string;
}

function BioscopeRestarts<TRestart extends BioscopeRestart | BioscopeRouterRestart>({
    loading,
    lastBioscopeRestarts,
    restartBioscope,
    stepMessage,
    disabled = false,
    disabledReason,
}: BioscopeRestartsProps<TRestart>) {
    const { t } = useTranslation();

    const columns: DataTableColumnDef<TRestart>[] = [
        {
            id: 'updatedAt',
            header: t('molecules.bioscopeRestarts.lastUpdate'),
            cell: ({ row }) => formatDateTimeWorded(row.original.updatedAt, 'minute'),
        },
        {
            id: 'isDone',
            header: t('molecules.bioscopeRestarts.isDone'),
            cell: ({ row }) =>
                row.original.isDone ? t('common.labels.yes') : t('common.labels.no'),
        },
        {
            id: 'step',
            header: t('molecules.bioscopeRestarts.step'),
            cell: ({ row }) => stepMessage(t, row.original),
        },
        {
            id: 'message',
            header: t('molecules.bioscopeRestarts.message'),
            cell: ({ row }) => row.original.message,
        },
    ];

    const [restartingBioscope, setRestartingBioscope] = useState<boolean>(false);
    const [restartBioscopeError, setRestartBioscopeError] = useState<ReactNode>(null);

    const wrappedRestartBioscope = async () => {
        try {
            setRestartingBioscope(true);
            await restartBioscope();
        } catch (e) {
            console.log(e);
            setRestartBioscopeError(apiErrorContent(e));
        } finally {
            setRestartingBioscope(false);
        }
    };

    return (
        <>
            <div className={styles.section}>
                <Button onClick={wrappedRestartBioscope} disabled={disabled}>
                    {restartingBioscope ? <Spinner /> : t('molecules.bioscopeRestarts.restart')}
                </Button>
                {disabled && disabledReason ? (
                    <p className={styles.disabledReason}>{disabledReason}</p>
                ) : null}
            </div>
            <div>
                {restartBioscopeError && <Alert tone="danger">{restartBioscopeError}</Alert>}
                <div className={styles.section}>
                    <DataTable
                        data={lastBioscopeRestarts ?? []}
                        columns={columns}
                        getRowId={(restart) => restart.updatedAt}
                        enableSorting={false}
                        hover={false}
                    />
                    {loading && <Spinner size="lg" />}
                </div>
            </div>
        </>
    );
}

export default BioscopeRestarts;
export { BioscopeRestarts };
